<template>
  <div class="map-settings-content">
    <div class="map-settings-row">
      <label for="setting-language">{{ $t('settings.language') }}</label>
      <select
        id="setting-language"
        class="map-settings-select"
        :value="currentLocale"
        @change="onLanguageChange"
      >
        <option v-for="lang in languages" :key="lang.value" :value="lang.value">{{ lang.label }}</option>
      </select>
    </div>

    <div class="map-settings-row">
      <label for="setting-theme">{{ $t('settings.theme') }}</label>
      <select
        id="setting-theme"
        class="map-settings-select"
        :value="realColorScheme"
        @change="onThemeChange"
      >
        <option value="light">{{ $t('settings.light') }}</option>
        <option value="dark">{{ $t('settings.dark') }}</option>
      </select>
    </div>

    <div class="map-settings-row">
      <label for="setting-timezone">{{ $t('settings.timezone') }}</label>
      <select
        id="setting-timezone"
        class="map-settings-select"
        :value="timeZonePreference"
        @change="onTimeZoneChange"
      >
        <option value="local">{{ $t('settings.local') }} ({{ browserTimeZone }})</option>
        <option
          v-for="tz in availableTimeZones"
          :key="tz"
          :value="tz"
        >
          {{ tz }}
        </option>
      </select>
    </div>

    <div class="map-settings-row map-settings-row--toggle">
      <label for="setting-colorblind-friendly">{{ $t('settings.colorblindFriendly') }}</label>
      <input
        id="setting-colorblind-friendly"
        class="map-settings-checkbox"
        type="checkbox"
        :checked="isColorblindFriendly"
        @change="onColorblindFriendlyChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useColorScheme } from '@/composables/useColorScheme';
import { useDateTimeSettings } from '@/composables/useDateTimeSettings';
import { useAccessibilitySettings } from '@/composables/useAccessibilitySettings';
import { setLocale } from '@/i18n';

const { locale } = useI18n();
const { setColorScheme, realColorScheme } = useColorScheme();
const {
  timeZonePreference,
  setTimeZonePreference,
  browserTimeZone,
  availableTimeZones,
} = useDateTimeSettings();
const { isColorblindFriendly, setColorblindFriendly } = useAccessibilitySettings();

const currentLocale = computed(() => locale.value);

const languages = [
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'Français' },
  { value: 'es', label: 'Español' },
  { value: 'pt', label: 'Português' },
];

function onLanguageChange(e: Event) {
  const value = (e.target as HTMLSelectElement).value;
  setLocale(value);
}

function onThemeChange(e: Event) {
  const value = (e.target as HTMLSelectElement).value;
  if (value === 'light' || value === 'dark') {
    setColorScheme(value);
  }
}

function onTimeZoneChange(e: Event) {
  const value = (e.target as HTMLSelectElement).value;
  setTimeZonePreference(value);
}

function onColorblindFriendlyChange(e: Event) {
  setColorblindFriendly((e.target as HTMLInputElement).checked);
}
</script>

<style scoped lang="scss">
.map-settings-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.map-settings-row {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.map-settings-row--toggle {
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.map-settings-row label {
  font-size: 0.82rem;
  color: var(--p-text-muted-color, #94a3b8);
}

.map-settings-checkbox {
  flex: 0 0 auto;
  width: 1.1rem;
  height: 1.1rem;
  margin-top: 0.05rem;
  accent-color: var(--p-primary-color, #00bfa5);
}

.map-settings-select {
  width: 100%;
  min-height: 34px;
  border-radius: 6px;
  border: 1px solid var(--p-content-border-color, rgba(255, 255, 255, 0.14));
  background: var(--p-content-background, #0f172a);
  color: var(--p-text-color, #e2e8f0);
  padding: 0.3rem 0.55rem;
  font-size: 0.85rem;
}
</style>
